import React, { useMemo, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";

import { Button } from "components/ui/Button";
import { DataTable } from "components/ui/DataTable";

import styles from "./DocumentTypesTable.module.css";
import { useColumns } from "./hooks/useColumns";
import { FILTER_GROUPS, SORT_OPTIONS } from "./utils/constants";

export const DocumentTypesTable = ({
  documentTypes = [],
  isLoading = false,
  onEdit,
  onDelete,
  onAdd,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState([]);
  const [sortField, setSortField] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);

  const filteredData = useMemo(() => {
    let result = documentTypes;

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter((dt) => dt.name?.toLowerCase().includes(q));
    }

    if (statusFilter.length > 0) {
      result = result.filter((dt) =>
        statusFilter.includes(dt.defaultNewStatus)
      );
    }

    result = [...result].sort((a, b) => {
      const nameA = (a.name || "").toLowerCase();
      const nameB = (b.name || "").toLowerCase();
      return sortDirection === "asc"
        ? nameA.localeCompare(nameB)
        : nameB.localeCompare(nameA);
    });

    return result;
  }, [documentTypes, searchQuery, statusFilter, sortDirection]);

  const columns = useColumns();

  const rowActions = useMemo(
    () => [
      {
        label: "Edit",
        icon: Pencil,
        onClick: (row) => onEdit?.(row),
        primary: true,
        primaryOnly: true,
      },
      {
        label: "Delete",
        icon: Trash2,
        onClick: (row) => onDelete?.(row),
      },
    ],
    [onEdit, onDelete]
  );

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <p className={styles.title}>Document Types</p>
        <Button variant="text" onClick={onAdd} endIcon={Plus}>
          Add new document type
        </Button>
      </div>
      <DataTable
        columns={columns}
        data={filteredData}
        getRowId={(row) => row.id}
        isLoading={isLoading}
        emptyTitle="No document types found"
        emptyText="There are no document types to display."
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        search={{
          value: searchQuery,
          onChange: (v) => { setSearchQuery(v); setCurrentPage(1); },
          placeholder: "Search by name",
        }}
        sort={{
          field: sortField,
          direction: sortDirection,
          onChange: ({ field, direction }) => {
            setSortField(field);
            setSortDirection(direction);
            setCurrentPage(1);
          },
        }}
        sortOptions={SORT_OPTIONS}
        filters={{
          value: { defaultNewStatus: statusFilter },
          onChange: (groupId, value) => {
            if (groupId === "defaultNewStatus") { setStatusFilter(value); setCurrentPage(1); }
          },
        }}
        filtersConfig={FILTER_GROUPS}
        rowActions={rowActions}
      />
    </div>
  );
};
